import React, {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import Link from 'router/Link';
import {useAuth0} from '@auth0/auth0-react';
import {userLogout} from 'store/user/user-actions';
import {Heading, Flex, Button, Text, Box} from '@spothero/ui';
import AuthenticationUtils from 'utils/authentication';
import Auth0ServiceInterceptor from 'auth/auth0-service-interceptor';

import ReservationUtils from 'utils/reservation-utils';
import UserUtils from 'utils/user-utils';

export const ORDER = 'order';
export const PARKING_PASS = 'parking pass';

const PostPurchaseAuthError = ({errorType, item}) => {
    const {data: user} = useSelector(state => state.user);
    const dispatch = useDispatch();
    const {isAuthenticated, logout, loginWithPopup} = useAuth0();
    const [loading, setLoading] = useState(false);
    const loggedInUser = user.status === UserUtils.AUTH_STATE.USER;
    let errorText = null;
    let subErrorText = null;
    let buttonValues = null;

    const onLoginClick = async () => {
        await loginWithPopup();
    };

    const onLogoutClick = () => {
        const isLoggedOut = user.status === UserUtils.AUTH_STATE.LOGGED_OUT;

        // if user is an auth0 user, encode the checkout url and pass that along as a query parameter
        if (isAuthenticated && !isLoggedOut) {
            setLoading(true);

            Auth0ServiceInterceptor.resetTokenGenerator();

            // ensure monolith cookies are removed and micro-frontend cookies are added
            AuthenticationUtils.configureLogoutCookies();

            logout({
                returnTo: `${
                    window.location.origin
                }/auth/checkout-redirect?next=${encodeURIComponent(
                    window.location.href
                )}`,
            });
        } else if (!isLoggedOut) {
            dispatch(userLogout(UserUtils.AUTH_STATE.GUEST));
        }
    };

    const onRefreshClick = () => {
        window.location.reload();
    };

    switch (errorType) {
        case ReservationUtils.POST_PURCHASE_ERRORS_ENUM.ACCESS_DENIED:
            if (loggedInUser) {
                errorText = `This ${item} is owned by another account.`;
                subErrorText = (
                    <Text>
                        If you have another account, try signing in to that one.
                    </Text>
                );
                buttonValues = {
                    text: 'Log Out',
                    props: {
                        variant: 'secondary',
                        onClick: onLogoutClick,
                        disabled: loading,
                    },
                };
            } else {
                errorText = `You'll need to sign in to access this ${item}.`;
                buttonValues = {
                    text: 'Sign In',
                    props: {
                        variant: 'primary',
                        onClick: onLoginClick,
                        disabled: loading,
                    },
                };
            }
            break;
        case ReservationUtils.POST_PURCHASE_ERRORS_ENUM.NO_ID:
        case ReservationUtils.POST_PURCHASE_ERRORS_ENUM.NOT_FOUND:
            errorText = `Hmm, we couldn't find this ${item}.`;
            subErrorText = (
                <Text>
                    You may have followed an invalid link or pasted an
                    incomplete URL.
                </Text>
            );
            buttonValues = {
                text: 'Go to My Reservations',
                props: {
                    as: 'a',
                    href: '/account-reservations',
                    variant: 'primary',
                },
            };
            break;

        default:
            errorText = `Oops, we had some trouble loading your ${item}.`;
            subErrorText = (
                <>
                    <Text>
                        Try refreshing this page or{' '}
                        <Link forceAnchor to="/account-reservations">
                            go to your Reservations
                        </Link>
                        .
                    </Text>
                    {item === ORDER && (
                        <Text>
                            Don&apos;t worry, you won&apos;t be charged twice.
                        </Text>
                    )}
                </>
            );
            buttonValues = {
                text: 'Refresh',
                props: {
                    variant: 'primary',
                    onClick: onRefreshClick,
                },
            };
            break;
    }

    return (
        <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="calc(100vh - 10rem)"
        >
            <Heading
                width="70%"
                textAlign="center"
                whiteSpace="pre-line"
                fontSize="xl"
                fontWeight="normal"
                as="h1"
                lineHeight={1.25}
            >
                {errorText}
            </Heading>
            {subErrorText ? (
                <Box marginTop={4} textAlign="center">
                    {subErrorText}
                </Box>
            ) : null}

            <Button marginTop={8} {...buttonValues.props}>
                {buttonValues.text}
            </Button>
        </Flex>
    );
};

PostPurchaseAuthError.propTypes = {
    errorType: PropTypes.oneOf(
        Object.values(ReservationUtils.POST_PURCHASE_ERRORS_ENUM)
    ),
    item: PropTypes.oneOf([ORDER, PARKING_PASS]),
};

export default PostPurchaseAuthError;
